import {
  isRelatorioEsocial,
  isRelatorioIcms,
} from "../../utils/relatorioApuracao.util";

/** Granularidade do período na UI de apuração. */
export const GRANULARIDADE_PERIODO = {
  ANO: "ano",
  MES: "mes",
};

/** Config SPED/V3 (e fallback): período anual, termos de exclusão e contas ECD. */
const CONFIG_SPED_LIKE = Object.freeze({
  granularidadePeriodo: GRANULARIDADE_PERIODO.ANO,
  labelPeriodoInicial: "Ano Inicial",
  labelPeriodoFinal: "Ano Final",
  msgPeriodoObrigatorio: "Preencha o período (ano inicial e final).",
  msgOrdemPeriodo: "O ano inicial não pode ser posterior ao ano final.",
  campos: Object.freeze({
    termosExclusao: true,
    contasCriterio: true,
  }),
  alerta5Anos: true,
});

/**
 * Config eSocial: competência mensal (YYYY-MM); sem termos nem contas de resultado.
 */
const CONFIG_ESOCIAL = Object.freeze({
  granularidadePeriodo: GRANULARIDADE_PERIODO.MES,
  labelPeriodoInicial: "Competência inicial",
  labelPeriodoFinal: "Competência final",
  msgPeriodoObrigatorio: "Preencha o período (competência inicial e final).",
  msgOrdemPeriodo:
    "A competência inicial não pode ser posterior à competência final.",
  campos: Object.freeze({
    termosExclusao: false,
    contasCriterio: false,
  }),
  alerta5Anos: true,
});

/**
 * TXM-418 — Config ICMS (registro E110/E111 do SPED Fiscal e CAT-42): competência
 * mensal, sem termos de exclusão nem contas de resultado (conceitos de SPED/V3).
 * Mesma granularidade do eSocial, mas config própria porque os rótulos e o
 * conjunto de campos podem divergir conforme as abas da tela unificada evoluem.
 */
const CONFIG_ICMS = Object.freeze({
  granularidadePeriodo: GRANULARIDADE_PERIODO.MES,
  labelPeriodoInicial: "Competência inicial",
  labelPeriodoFinal: "Competência final",
  msgPeriodoObrigatorio: "Preencha o período (competência inicial e final).",
  msgOrdemPeriodo:
    "A competência inicial não pode ser posterior à competência final.",
  campos: Object.freeze({
    termosExclusao: false,
    contasCriterio: false,
  }),
  alerta5Anos: true,
});

/**
 * Resolve a config de UI/validação da modal de apuração pelo código do relatório.
 * @param {string | null | undefined} codigoRelatorio
 */
export function configRelatorioApuracao(codigoRelatorio) {
  if (isRelatorioEsocial(codigoRelatorio)) {
    return CONFIG_ESOCIAL;
  }
  if (isRelatorioIcms(codigoRelatorio)) {
    return CONFIG_ICMS;
  }
  return CONFIG_SPED_LIKE;
}

/**
 * Converte período entre granularidades (ano ↔ mês) preservando o ano quando possível.
 * @param {string} periodoIni
 * @param {string} periodoFim
 * @param {"ano"|"mes"} granularidade
 * @returns {{ ini: string, fim: string }}
 */
export function adaptarPeriodoParaGranularidade(
  periodoIni,
  periodoFim,
  granularidade
) {
  const ini = String(periodoIni ?? "").trim();
  const fim = String(periodoFim ?? "").trim();

  if (granularidade === GRANULARIDADE_PERIODO.MES) {
    const toYm = (v, fallbackMes) => {
      if (/^\d{4}-\d{2}$/.test(v)) return v;
      if (/^\d{4}$/.test(v)) return `${v}-${fallbackMes}`;
      return "";
    };
    const nextIni = toYm(ini, "01");
    const nextFim = toYm(fim, "12");
    if (nextIni && nextFim) return { ini: nextIni, fim: nextFim };
    const agora = new Date();
    const mes = String(agora.getMonth() + 1).padStart(2, "0");
    const ym = `${agora.getFullYear()}-${mes}`;
    return { ini: ym, fim: ym };
  }

  const toAno = (v) => {
    if (/^\d{4}$/.test(v)) return v;
    if (/^\d{4}-\d{2}/.test(v)) return v.slice(0, 4);
    return "";
  };
  const nextIni = toAno(ini);
  const nextFim = toAno(fim);
  if (nextIni && nextFim) return { ini: nextIni, fim: nextFim };
  const y = String(new Date().getFullYear());
  return { ini: y, fim: y };
}
